import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { BackendError, BackendValidationError } from './backend-error';
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ErrorDialogComponent, ErrorDialogData } from "../components/common/dialog/error-dialog/error-dialog.component";
import { MODAL_DATA } from '../injection-token/modal-data';
import { tap } from "rxjs";
import * as Sentry from "@sentry/angular";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable({providedIn: 'root'})
export class ExceptionHandler extends Sentry.SentryErrorHandler {
  private ngZone = inject(NgZone);
  private injector = inject(Injector);


  dialogRef: NgbModalRef | null = null;

  override handleError(error: unknown): void {
    const errorData: ErrorDialogData = this.getErrorData(error);

    this.ngZone.run(() => {
      this.openDialog(errorData);
    });
    super.handleError(error);
  }

  openDialog(errorData: ErrorDialogData): void {
    if (this.dialogRef) {
      return
    }
    const dialog = this.injector.get(NgbModal);
    this.dialogRef = dialog.open(ErrorDialogComponent, {
      injector: Injector.create({providers: [{provide: MODAL_DATA, useValue: errorData}]}),
      size: 'lg',
    });
    this.dialogRef.closed.pipe(
      tap(() => {
        this.dialogRef = null;
      })
    ).subscribe();
  }

  private getErrorData(error: unknown): ErrorDialogData {
    if (typeof error === 'string') {
      return {
        title: $localize`Błąd`,
        message: error,
      }
    }
    if (error == undefined || typeof error !== 'object') {
      return {
        title: $localize`Błąd`,
        message: $localize`Wystąpił błąd podczas przetwarzania żądania`,
      }
    }
    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof BackendError) {
        const backedError: BackendError = error.error;
        const title = backedError.error;
        const message = backedError.localizedMessage ?? backedError.message;
        const stacktrace = backedError.trace;
        const path = backedError.path;
        const status = backedError.status;

        if (error.error instanceof BackendValidationError) {
          return {
            title,
            message: this.getValidationErrorMessage(error.error),
            stacktrace,
            path,
            status
          }
        }

        return {
          title,
          message,
          stacktrace,
          path,
          status
        }
      }

      return {
        title: $localize`Błąd`,
        message: error.message,
        status: error.status,
        path: error.url
      }
    }

    if (error instanceof Error) {
      return {
        title: error.name,
        message: error.message,
        stacktrace: error.stack,
        path: null
      };
    }

    return {
      title: $localize`Błąd`,
      message: $localize`Wystąpił błąd podczas przetwarzania żądania`,
    }
  }

  private getValidationErrorMessage(validationError: BackendValidationError): string {
    return validationError.errors.map(error => `Pole: ${error.field} Błąd: ${error.defaultMessage}`).join('\n\n');
  }
}
